'use client'

import Script from 'next/script'

type MatomoTagManagerEvent = {
  'mtm.startTime': number
  event: 'mtm.Start'
}

type MatomoTrackerCommand = [command: string, ...args: unknown[]]

declare global {
  interface Window {
    /*
     * `_mtm` is the Tag Manager API
     */
    _mtm: MatomoTagManagerEvent[]
    /**
     * `_paq` is the low level JS tracker API (see https://forum.matomo.org/t/confusion-about-paq-push-and-mtm-push/56178)
     */
    _paq: MatomoTrackerCommand[]
    mtmStarted?: boolean
  }
}

import { env } from '@/env'
import { useUser } from '@clerk/nextjs'
import { Suspense, useEffect, useLayoutEffect } from 'react'

function initializeMatomoGlobals() {
  window._mtm = window._mtm || []
  window._paq = window._paq || []

  if (!window.mtmStarted) {
    window._mtm.push({
      'mtm.startTime': Date.now(),
      event: 'mtm.Start',
    })
    window.mtmStarted = true
  }
}

/**
 * Insert Matomo Tag Manager to track page views
 * @see https://developer.matomo.org/guides/spa-tracking
 */
export default function Matomo() {
  // Boot Matomo globals before child effects queue tracker calls.
  useLayoutEffect(() => {
    initializeMatomoGlobals()
  }, [])

  return (
    <>
      <Script
        id='matomo-container'
        src={`https://cdn.matomo.cloud/gesetzefinden.matomo.cloud/container_aEvKBgvb${env.NEXT_PUBLIC_VERCEL_ENV !== 'production' ? '_dev_690b41c73c4ef3b0339f19f5' : ''}.js?v3`}
        strategy='afterInteractive'
        onLoad={() => {
          console.log('Finished loading matomo script')
        }}
        onError={(error) => {
          console.log('Could not load Matomo script:', error)
        }}
      />
      <Suspense>
        <InitUserId />
      </Suspense>
    </>
  )
}

function InitUserId() {
  const { isLoaded, user } = useUser()
  const userId = user?.primaryEmailAddress?.emailAddress

  useEffect(() => {
    initializeMatomoGlobals()

    if (!isLoaded) return

    if (userId) {
      window._paq.push(['setUserId', userId])
    } else {
      window._paq.push(['resetUserId'])
    }
  }, [isLoaded, userId])

  return null
}
